import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  CheckCircle, 
  XCircle, 
  Eye, 
  Clock, 
  UserPlus, 
  CheckCircle2, 
  Trash2,
  AlertTriangle,
  Filter,
  Search,
  X
} from 'lucide-react';
import { usersAPI } from '../../services/api';
import { toast } from 'sonner';
import LoadingSpinner from '../common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

const PendingApprovalsTab = () => {
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Debug: Log current user data
  console.log('Debug - Current user in PendingApprovalsTab:', currentUser);
  console.log('Debug - Current user ID (_id):', currentUser?._id);
  console.log('Debug - Current user ID (id):', currentUser?.id);
  console.log('Debug - Current user email:', currentUser?.email);

  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);

  const queryClient = useQueryClient();

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Fetch pending approval requests
  const { data: pendingRequests, isLoading, error } = useQuery({
    queryKey: ['pendingApprovalRequests', debouncedSearchTerm, filterAction],
    queryFn: () => usersAPI.getPendingApprovalRequests({
      search: debouncedSearchTerm || undefined,
      actionType: filterAction === 'all' ? undefined : filterAction
    }),
    select: (data) => {
      // The actual structure is data.data.data.requests (3 levels deep!)
      const requests = data?.data?.data?.requests || data?.data?.requests || data?.requests || [];
      console.log('Debug - Pending requests data:', requests);
      return requests;
    }
  });

  // Approve L2 request mutation
  const approveMutation = useMutation({
    mutationFn: ({ id }) => usersAPI.approveL2Request(id, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingApprovalRequests'] });
      queryClient.invalidateQueries({ queryKey: ['clientUsers'] });
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      toast.success('Request approved successfully');
      setShowApprovalModal(false);
      setSelectedRequest(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to approve request');
    }
  });

  // Reject L2 request mutation
  const rejectMutation = useMutation({
    mutationFn: ({ id }) => usersAPI.rejectL2Request(id, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingApprovalRequests'] });
      queryClient.invalidateQueries({ queryKey: ['clientUsers'] });
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      toast.success('Request rejected successfully');
      setShowRejectionModal(false);
      setSelectedRequest(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reject request');
    }
  });

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setShowApprovalModal(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setShowRejectionModal(true);
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  // Check if current user is the initiator of the request
  const isCurrentUserInitiator = (request) => {
    if (!currentUser || !request.initiatorId) return false;
    
    const currentUserId = currentUser.id || currentUser._id;
    const initiatorId = request.initiatorId._id || request.initiatorId;
    
    console.log('Debug - isCurrentUserInitiator:', {
      currentUserId,
      initiatorId,
      requestInitiatorId: request.initiatorId,
      isMatch: currentUserId === initiatorId
    });
    
    return currentUserId === initiatorId;
  };

  const submitApproval = () => {
    approveMutation.mutate({ 
      id: selectedRequest._id
    });
  };

  const submitRejection = () => {
    rejectMutation.mutate({ 
      id: selectedRequest._id
    });
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'create':
        return <UserPlus className="h-4 w-4 text-blue-600" />;
      case 'activate':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'deactivate':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'delete':
        return <Trash2 className="h-4 w-4 text-red-600" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getActionLabel = (actionType) => {
    switch (actionType) {
      case 'create':
        return 'Create User';
      case 'activate':
        return 'Activate User';
      case 'deactivate':
        return 'Deactivate User';
      case 'delete':
        return 'Delete User';
      default:
        return actionType;
    }
  };

  const getUserDisplayName = (user) => {
    if (user?.personalInfo?.firstName && user?.personalInfo?.lastName) {
      return `${user.personalInfo.firstName} ${user.personalInfo.lastName}`;
    }
    if (user?.personalInfo?.fullName) {
      return user.personalInfo.fullName;
    }
    if (user?.corporateInfo?.companyName) {
      return user.corporateInfo.companyName;
    }
    return user?.email || 'N/A';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600">Failed to load pending approvals</p>
      </div>
    );
  }

  const filteredRequests = pendingRequests.filter(request => {
    if (filterAction !== 'all' && request.actionType !== filterAction) {
      return false;
    }
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const userName = (request.userDisplayData?.firstName + ' ' + request.userDisplayData?.lastName || request.userDisplayData?.companyName || '').toLowerCase();
      const userEmail = request.userDisplayData?.email?.toLowerCase() || '';
      const userRole = request.userDisplayData?.role?.toLowerCase() || '';
      const initiatorEmail = request.initiatorId?.email?.toLowerCase() || '';
      const comments = request.comments?.toLowerCase() || '';
      
      return userName.includes(searchLower) || 
             userEmail.includes(searchLower) || 
             userRole.includes(searchLower) ||
             initiatorEmail.includes(searchLower) ||
             comments.includes(searchLower);
    }
    return true;
  });



  return (
    <div className="space-y-6">
      {/* Header */}
     

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 bg-white border border-light-gray rounded-lg p-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search by user name, email, or initiator..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input pl-10"          />
        </div>
        <div className="sm:w-48">
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="form-input"          >
            <option value="all">All Actions</option>
            <option value="create">Create User</option>
            <option value="activate">Activate User</option>
            <option value="deactivate">Deactivate User</option>
            <option value="delete">Delete User</option>
          </select>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No pending approval requests</p>
            <p className="text-gray-400 text-sm mt-1">
              {searchTerm || filterAction !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'All requests have been processed'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
             <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
             <div className="px-4 py-5 sm:px-6">
                 <h2 className="text-lg leading-6 font-medium text-gray-900">Pending Approvals (L2)</h2>
        </div>
        <div className="text-sm text-gray-500 px-6">
          {filteredRequests.length} pending request{filteredRequests.length !== 1 ? 's' : ''}
        </div>
      </div>
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">
                    Action
                  </th>
                  <th className="table-header-cell">
                    User Information
                  </th>
                  <th className="table-header-cell">
                    Initiated By
                  </th>
                  <th className="table-header-cell">
                    Date
                  </th>
                  <th className="table-header-cell">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((request) => (
                  <tr key={request._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getActionIcon(request.actionType)}
                        <span className="text-sm font-medium text-gray-900">
                          {getActionLabel(request.actionType)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {request.userDisplayData?.firstName && request.userDisplayData?.lastName 
                          ? `${request.userDisplayData.firstName} ${request.userDisplayData.lastName}`
                          : request.userDisplayData?.companyName || request.userDisplayData?.email || 'N/A'
                        }
                      </div>
                      <div className="text-sm text-gray-500">
                        {request.userDisplayData?.email || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-400">
                        {request.userDisplayData?.role || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {request.initiatorId?.email || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(request.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-start space-x-2">
                        <button
                          onClick={() => handleViewDetails(request)}
                          className="text-primary-600 hover:text-primary p-1"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {(() => {
                          const isInitiator = isCurrentUserInitiator(request);
                          console.log('Debug - Button render for request:', {
                            requestId: request._id,
                            isInitiator,
                            currentUser: currentUser?._id,
                            initiatorId: request.initiatorId?._id || request.initiatorId
                          });
                          
                          // Hide buttons for initiator instead of disabling
                          if (isInitiator) {
                            return null;
                          }
                          
                          return (
                            <>
                              <button
                                onClick={() => handleApprove(request)}
                                className="btn-primary btn-sm"
                                title="Approve"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleReject(request)}
                                className="btn-secondary btn-sm"
                                title="Reject"
                              >
                                Reject
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Approve Request
              </h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Action:</strong> {getActionLabel(selectedRequest?.actionType)}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>User:</strong> {getUserDisplayName(selectedRequest?.userData)}
                </p>
                {/* <p className="text-sm text-gray-600">
                  <strong>Requested by:</strong> {getUserDisplayName(selectedRequest?.initiatedBy)}
                </p> */}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowApprovalModal(false)}
                  disabled={approveMutation.isPending}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={submitApproval}
                  disabled={approveMutation.isPending}
                  className="btn-primary"
                >
                  {approveMutation.isPending ? 'Approving...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Reject Request
              </h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Action:</strong> {getActionLabel(selectedRequest?.actionType)}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>User:</strong> {getUserDisplayName(selectedRequest?.userData)}
                </p>
              
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowRejectionModal(false)}
                  disabled={rejectMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submitRejection}
                  disabled={rejectMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {rejectMutation.isPending ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Request Details
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-8">
                {/* Basic Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Action Type</label>
                      <p className="text-sm text-gray-900">{getActionLabel(selectedRequest?.actionType)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Status</label>
                      <p className="text-sm text-gray-900">Pending L2 Approval</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Requested At</label>
                      <p className="text-sm text-gray-900">{formatDate(selectedRequest?.createdAt)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Requested By</label>
                      <p className="text-sm text-gray-900">{selectedRequest?.initiatorId?.email || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Comments</label>
                      <p className="text-sm text-gray-900">{selectedRequest?.comments || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* User Information */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">User Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Email</label>
                      <p className="text-sm text-gray-900">{selectedRequest?.userData?.email || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Role</label>
                      <p className="text-sm text-gray-900">{selectedRequest?.userData?.role || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Account Type</label>
                      <p className="text-sm text-gray-900">{selectedRequest?.userData?.accountType || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Account Status</label>
                      <p className="text-sm text-gray-900">{selectedRequest?.userData?.accountStatus || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Corporate Information */}
                {selectedRequest?.userData?.corporateInfo && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Corporate Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Company Name</label>
                        <p className="text-sm text-gray-900">{selectedRequest?.userData?.corporateInfo?.companyName || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Legal Entity Name</label>
                        <p className="text-sm text-gray-900">{selectedRequest?.userData?.corporateInfo?.legalEntityName || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Primary Account Holder</label>
                        <p className="text-sm text-gray-900">{selectedRequest?.userData?.corporateInfo?.primaryAccountHolderName || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Business Registration Number</label>
                        <p className="text-sm text-gray-900">{selectedRequest?.userData?.corporateInfo?.businessRegistrationNumber || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Registration Type</label>
                        <p className="text-sm text-gray-900">{selectedRequest?.userData?.corporateInfo?.registrationType || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Date of Incorporation</label>
                        <p className="text-sm text-gray-900">
                          {selectedRequest?.userData?.corporateInfo?.dateOfIncorporation 
                            ? new Date(selectedRequest?.userData?.corporateInfo?.dateOfIncorporation).toLocaleDateString()
                            : 'N/A'
                          }
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Registered Address</label>
                        <p className="text-sm text-gray-900">{selectedRequest?.userData?.corporateInfo?.registeredAddress || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Operating Address</label>
                        <p className="text-sm text-gray-900">{selectedRequest?.userData?.corporateInfo?.operatingAddress || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Phone Number</label>
                        <p className="text-sm text-gray-900">{selectedRequest?.userData?.corporateInfo?.phoneNumber || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Website</label>
                        <p className="text-sm text-gray-900">{selectedRequest?.userData?.corporateInfo?.website || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Business Nature</label>
                        <p className="text-sm text-gray-900">{selectedRequest?.userData?.corporateInfo?.businessNature || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Tax ID Number</label>
                        <p className="text-sm text-gray-900">{selectedRequest?.userData?.corporateInfo?.taxIdNumber || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Annual Revenue Range</label>
                        <p className="text-sm text-gray-900">{selectedRequest?.userData?.corporateInfo?.annualRevenue || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Number of Employees</label>
                        <p className="text-sm text-gray-900">{selectedRequest?.userData?.corporateInfo?.numberOfEmployees || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Representatives Information */}
                {selectedRequest?.userData?.representatives && selectedRequest?.userData?.representatives.length > 0 && (
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Representatives</h4>
                    <div className="space-y-3">
                      {selectedRequest?.userData?.representatives.map((rep, index) => (
                        <div key={index} className="border border-yellow-200 rounded-md p-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm font-medium text-gray-700">Full Name</label>
                              <p className="text-sm text-gray-900">{rep.fullName || 'N/A'}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Email</label>
                              <p className="text-sm text-gray-900">{rep.email || 'N/A'}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Phone Number</label>
                              <p className="text-sm text-gray-900">{rep.phoneNumber || 'N/A'}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">NRIC/Passport</label>
                              <p className="text-sm text-gray-900">{rep.nricPassport || 'N/A'}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Position</label>
                              <p className="text-sm text-gray-900">{rep.position || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingApprovalsTab;
